// src/lib/measurement/problemFactories/capacity.ts

import { Rng } from '../../rng';
import { formatWithUnit, makeNearbyNumberOptions, makeUnitOptions, makeLetterOptions } from '../distractors';
import type { MeasurementProblem } from './length';

type Difficulty = 1 | 2 | 3;

const BEST_UNIT_ITEMS: Array<{ item: string; unit: 'mL' | 'L' }> = [
  { item: 'teaspoon of medicine', unit: 'mL' },
  { item: 'water bottle', unit: 'mL' },
  { item: 'bucket', unit: 'L' },
  { item: 'milk carton', unit: 'L' },
  { item: 'swimming pool', unit: 'L' },
];

export function makeCapacityBestUnit(rng: Rng, id: string, difficulty: Difficulty): MeasurementProblem {
  void difficulty;
  const { item, unit } = rng.pick(BEST_UNIT_ITEMS);
  const expected = unit === 'mL' ? 'mL' : 'L';

  return {
    id,
    kind: 'mcq',
    type: 'capacity_best_unit',
    prompt: `What is the best unit to measure the capacity of a ${item}?`,
    choices: makeUnitOptions('mL', 'L'),
    meta: {
      topicArea: 'capacity',
      difficulty,
      expected,
      templateKey: 'capacity_best_unit',
      data: { item },
    },
  };
}

export function makeCapacityConversionMlL(rng: Rng, id: string, difficulty: Difficulty): MeasurementProblem {
  void difficulty;
  const dir = rng.chance(0.5) ? 'ml_to_l' : 'l_to_ml';

  if (dir === 'ml_to_l') {
    const ml = rng.pick([500, 1000, 2000] as const);
    const expected = ml === 500 ? 0.5 : ml / 1000;
    return {
      id,
      kind: 'input',
      type: 'capacity_conversion_ml_l',
      prompt: `Convert ${ml} mL to L.`,
      meta: {
        topicArea: 'capacity',
        difficulty,
        expected,
        unitHint: 'L',
        templateKey: 'capacity_conversion_ml_l:ml_to_l',
        data: { dir, ml },
      },
    };
  }

  const l = rng.pick([0.5, 1, 2] as const);
  const expected = l === 0.5 ? 500 : l * 1000;
  return {
    id,
    kind: 'input',
    type: 'capacity_conversion_ml_l',
    prompt: `Convert ${l} L to mL.`,
    meta: {
      topicArea: 'capacity',
      difficulty,
      expected,
      unitHint: 'mL',
      templateKey: 'capacity_conversion_ml_l:l_to_ml',
      data: { dir, l },
    },
  };
}

export function makeCapacityFillCount(rng: Rng, id: string, difficulty: Difficulty): MeasurementProblem {
  void difficulty;
  const jugL = rng.pick([1, 2, 3, 4, 5] as const);
  const jugMl = jugL * 1000;

  const cupCandidates = [100, 125, 200, 250, 500] as const;
  let cupMl = 250;
  let count = 0;

  let guard = 0;
  while (guard < 200) {
    guard++;
    const c = rng.pick(cupCandidates);
    const k = jugMl / c;
    if (Number.isInteger(k) && k > 1 && k <= 20) {
      cupMl = c;
      count = k;
      break;
    }
  }

  const expected = String(count);
  const choices = makeNearbyNumberOptions(rng, count, null, 1, 4);

  return {
    id,
    kind: 'mcq',
    type: 'capacity_fill_count',
    prompt: `A jug holds ${jugL} L. A cup holds ${cupMl} mL. How many cups are needed to fill the jug?`,
    choices,
    meta: {
      topicArea: 'capacity',
      difficulty,
      expected,
      templateKey: 'capacity_fill_count',
      data: { jugL, cupMl },
    },
  };
}

export function makeCapacityCompareContainers(rng: Rng, id: string, difficulty: Difficulty): MeasurementProblem {
  void difficulty;
  const ask: 'most' | 'least' = rng.chance(0.5) ? 'most' : 'least';

  const base = rng.pick([250, 500, 750, 1000, 1500, 2000] as const);
  const vals = rng.shuffle([base, base + 250, base + 500]).slice(0, 3);

  const labels = ['A', 'B', 'C'] as const;
  const items = labels.map((l, i) => ({ l, ml: vals[i] }));

  const best = items.reduce((acc, x) => (ask === 'most' ? (x.ml > acc.ml ? x : acc) : x.ml < acc.ml ? x : acc), items[0]);
  const expected = best.l;

  const figureLabels = items.map((it) => ({ label: it.l as 'A' | 'B' | 'C', value: `${it.ml} mL` }));

  return {
    id,
    kind: 'mcq',
    type: 'capacity_compare_containers',
    prompt: `Which container holds the ${ask === 'most' ? 'most' : 'least'}?`,
    choices: makeLetterOptions(expected, ['A', 'B', 'C']),
    meta: {
      topicArea: 'capacity',
      difficulty,
      expected,
      templateKey: 'capacity_compare_containers',
      figure: { kind: 'containers', labels: figureLabels, ask },
      data: { items, ask },
    },
  };
}
